import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  createPlatformUser,
  deletePlatformUser,
  fetchOrganizations,
  fetchPlatformUsers,
  updatePlatformUser,
} from "@/lib/platformApi";
import {
  formatLastLogin,
  formatUserSeats,
  orgUserRoleLabel,
  orgUserStatusLabel,
  ORG_BUYER_OWNER_ROLE_OPTIONS,
} from "@/lib/platformOrgUserLabels";
import { Loader2, Pencil, Trash2, UserX, UserCheck, Info } from "lucide-react";

const EMPTY_EDIT = { name: "", email: "", role: "operador", password: "", status: "active" };

export default function PlatformUsers() {
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterOrgId, setFilterOrgId] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("admin_cliente");
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [o, u] = await Promise.all([
        fetchOrganizations(),
        fetchPlatformUsers(filterOrgId || undefined),
      ]);
      setOrgs(o);
      setUsers(u);
      if (!organizationId && o[0]?.id) setOrganizationId(o[0].id);
    } catch {
      toast.error("Error cargando usuarios");
    }
  }, [filterOrgId, organizationId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const orgNameById = useMemo(() => {
    const m = {};
    orgs.forEach((o) => {
      m[o.id] = o.name;
    });
    return m;
  }, [orgs]);

  const onCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await createPlatformUser({
        organization_id: organizationId,
        email: email.trim().toLowerCase(),
        name: name.trim(),
        password,
        role,
        install_pending_license: true,
      });
      toast.success("Usuario creado");
      setEmail("");
      setName("");
      setPassword("");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear usuario");
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({
      name: u.name || "",
      email: u.email || "",
      role: u.bnt_org_role || (u.role === "admin" ? "admin_cliente" : u.role === "user" ? "operador" : u.role) || "operador",
      password: "",
      status: u.status || "active",
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editTarget?.id) return;
    setBusy(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        email: editForm.email.trim().toLowerCase(),
        role: editForm.role,
        status: editForm.status,
      };
      if (editForm.password.trim()) {
        payload.password = editForm.password;
      }
      await updatePlatformUser(editTarget.id, payload);
      toast.success("Usuario actualizado");
      setEditOpen(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar");
    } finally {
      setBusy(false);
    }
  };

  const toggleSuspend = async (u) => {
    const next = u.status === "suspended" ? "active" : "suspended";
    setBusy(true);
    try {
      await updatePlatformUser(u.id, { status: next });
      toast.success(next === "suspended" ? "Usuario suspendido" : "Usuario reactivado");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cambiar estado");
    } finally {
      setBusy(false);
    }
  };

  const openDelete = (u) => {
    setDeleteTarget(u);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setBusy(true);
    try {
      await deletePlatformUser(deleteTarget.id);
      toast.success("Usuario eliminado");
      setDeleteOpen(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-blue-100 bg-blue-50/60 px-4 py-3 text-sm text-blue-900 flex gap-2">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <strong>Compradores / titulares de organización</strong> — solo cuentas titular con
          cupos de licencia (<code>autorizados</code>). Los auxiliares del despacho se administran en
          el entorno del comprador: <strong>Panel General → Actividades de Equipo</strong>. Los
          usuarios internos BNT van en <strong>Usuarios Administradores BNT</strong>.
        </div>
      </div>

      <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm">
        <h2 className="text-base font-semibold text-[#0F2D5C]">Alta de usuario</h2>
        <form onSubmit={onCreate} className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Organización</Label>
            <select
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              required
            >
              <option value="">Seleccione…</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} ({o.rfc})
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Nombre</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <Label>Correo</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <Label>Contraseña</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <div>
            <Label>Rol</Label>
            <select
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {ORG_BUYER_OWNER_ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={busy} className="bg-[#0F2D5C] hover:bg-[#0a2247]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear usuario"}
            </Button>
          </div>
        </form>
      </section>

      <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm overflow-x-auto">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <h2 className="text-base font-semibold text-[#0F2D5C]">Compradores y cupos</h2>
          <div>
            <Label className="text-xs">Filtrar por organización</Label>
            <select
              className="mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm min-w-[200px]"
              value={filterOrgId}
              onChange={(e) => setFilterOrgId(e.target.value)}
            >
              <option value="">Todas</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {users.length === 0 ? (
          <p className="text-sm text-slate-500">Sin usuarios de organizaciones.</p>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-slate-500">
                <th className="py-2 pr-2">Nombre</th>
                <th className="py-2 pr-2">Correo</th>
                <th className="py-2 pr-2">Organización</th>
                <th className="py-2 pr-2">Rol</th>
                <th className="py-2 pr-2">Cupos usuarios</th>
                <th className="py-2 pr-2">Estado</th>
                <th className="py-2 pr-2">Último acceso</th>
                <th className="py-2 pr-2">Titular</th>
                <th className="py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 align-top">
                  <td className="py-2 pr-2 font-medium text-[#0F2D5C]">{u.name}</td>
                  <td className="py-2 pr-2">{u.email}</td>
                  <td className="py-2 pr-2">{u.organization_name || orgNameById[u.organization_id] || "—"}</td>
                  <td className="py-2 pr-2">{u.role_label || orgUserRoleLabel(u.bnt_org_role, u.role)}</td>
                  <td className="py-2 pr-2 text-xs font-mono" title="Activos / autorizados por licencia">
                    {formatUserSeats(u)}
                  </td>
                  <td className="py-2 pr-2">
                    <span
                      className={
                        u.status === "suspended"
                          ? "text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded text-xs"
                          : "text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded text-xs"
                      }
                    >
                      {orgUserStatusLabel(u.status)}
                    </span>
                  </td>
                  <td className="py-2 pr-2 text-xs text-slate-600">{formatLastLogin(u.last_login_at)}</td>
                  <td className="py-2 pr-2 text-xs">{u.is_primary_owner ? "Sí" : "—"}</td>
                  <td className="py-2">
                    <div className="flex flex-wrap gap-1">
                      {u.can_edit !== false && (
                        <Button type="button" variant="outline" size="sm" onClick={() => openEdit(u)} title="Modificar">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {u.can_suspend && u.status !== "suspended" && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => toggleSuspend(u)}
                          title="Suspender"
                        >
                          <UserX className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {u.status === "suspended" && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => toggleSuspend(u)}
                          title="Reactivar"
                        >
                          <UserCheck className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {u.can_delete ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-red-700 border-red-200"
                          onClick={() => openDelete(u)}
                          title="Eliminar"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        u.delete_block_reason && (
                          <span
                            className="text-[10px] text-slate-500 max-w-[140px] inline-block"
                            title={u.delete_block_reason}
                          >
                            {u.delete_block_reason.slice(0, 48)}
                            {u.delete_block_reason.length > 48 ? "…" : ""}
                          </span>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modificar usuario</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>Nombre</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Correo</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Nueva contraseña (opcional)</Label>
              <Input
                type="password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                placeholder="Dejar vacío para no cambiar"
              />
            </div>
            <div>
              <Label>Rol</Label>
              <select
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
              >
              {ORG_BUYER_OWNER_ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
              </select>
            </div>
            <div>
              <Label>Estado</Label>
              <select
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              >
                <option value="active">Activo</option>
                <option value="suspended">Suspendido</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={submitEdit} disabled={busy} className="bg-[#0F2D5C]">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar usuario</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            ¿Eliminar a <strong>{deleteTarget?.email}</strong>? Esta acción registra tombstone comercial
            y no se puede deshacer fácilmente.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={busy}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
